/**
 * leaderboard/api.ts — the browser's client for the weekly Tool Leaderboard backend
 * (GET /api/leaderboard, RT §6). Reuses the generic same-origin helpers from community/api.ts (the
 * session cookie is the only credential — no token in the browser, ever) and adds the one typed call
 * the Top-5 section makes. The response interfaces mirror the backend SAFE shapes exactly
 * (db/leaderboard.ts LeaderboardView/LeaderboardRow/LeaderboardCard/Movement).
 *
 * The leaderboard route is AUTHENTICATED and ALWAYS mounted, so a 401 is a real signed-out state and
 * any other non-OK is a real error — we use the no-feature result union. Callers treat the whole
 * fetch as NON-FATAL: a signed-out visitor or any error degrades to an empty view (RT-F06), never a
 * crashed page.
 *
 * ANTI-RANKING OF PEOPLE + PRIVACY (RT-F22 / RT-NFR05): every field here is a COUNT or an AVERAGE over
 * a tool — never a per-person figure, never WHO adopted/liked/rated. The shapes below carry no such
 * field, by design.
 */
import { apiGet, type ApiResult } from '../community/api';

/** The RT-F04 movement vocabulary, frozen at recompute time and passed through the read path unchanged. */
export type Movement = 'new' | 'reentry' | 'up' | 'down' | 'unchanged';

/** The RT-F08 card contents for a tool — all AGGREGATE, never per-person (RT-NFR05). */
export interface LeaderboardCard {
  toolId: string;
  name: string;
  description: string;
  ownerName: string;
  primaryCategoryLabel: string | null;
  healthState: string;
  adoptionCount: number;
  likeCount: number;
  ratingAverage: number | null;
  ratingCount: number;
}

/** A card plus its frozen ranked position/score/movement (RT-F03/F04). */
export interface LeaderboardRow extends LeaderboardCard {
  position: number;
  score: number;
  movement: Movement;
  movementPlaces: number;
}

/** The whole view the route returns: the ordered survivors, when it last ran, and the one-liner (RT-NFR08). */
export interface LeaderboardView {
  entries: LeaderboardRow[];
  lastRecalculatedAt: string;
  explanation: string;
}

/** An empty view (no entries), still carrying an empty explanation/last-updated. The graceful-degrade
 * value every caller falls back to when signed out or on any error (RT-F06). */
export const EMPTY_LEADERBOARD_VIEW: LeaderboardView = {
  entries: [],
  lastRecalculatedAt: '',
  explanation: '',
};

/** GET /api/leaderboard — the current weekly Top 5 for the viewer (live-eligible + re-numbered). */
export function getLeaderboard(): Promise<ApiResult<LeaderboardView>> {
  return apiGet<LeaderboardView>('/api/leaderboard');
}

/**
 * The AUTHORITATIVE movement text (RT-F04 / RT-NFR04). Movement is shown IN TEXT — this string stands
 * alone; any arrow/colour a caller adds is decorative only and must never be the sole signal (WCAG,
 * never colour-only). `places` is the frozen movement_places from the backend.
 */
export function movementText(movement: Movement, places: number): string {
  switch (movement) {
    case 'new':
      return 'New this week';
    case 'reentry':
      return 'Re-entered';
    case 'up':
      return `Up ${places}`;
    case 'down':
      return `Down ${places}`;
    case 'unchanged':
      return 'No change';
  }
}
